import { ILoginResponse, ISignupPayload } from "@/_models/auth";
import { ApiErrorResponse } from "@/_models/common";
import { removeCookie, setCookie } from "@/app/api/jwt/fetcher";
import { mainApiInstance } from "../_axiosInstance";
import { useAuthStore } from "@/_stores/useAuthStore";
import { signOut } from "next-auth/react";
import {
  BIEvents,
  trackEvent,
} from "@/_components/lib/mixpanel/mixpanelClient";

const login = async (email: string, password: string) => {
  try {
    const response = await mainApiInstance.post<
      ILoginResponse | ApiErrorResponse
    >(`/auth/sign-in`, {
      email,
      password,
    });
    const data = response.data as ILoginResponse;
    await setCookie(data.token, data.userId, data.expiredAt, data.roleId);
    useAuthStore
      .getState()
      .setAuth(data.token, data.userId, data.expiredAt, data.roleId);
    return data;
  } catch (error) {
    throw error as ApiErrorResponse;
  }
};

const forgotPassword = async (email: string) => {
  const response = await mainApiInstance.post<ApiErrorResponse>(
    `/auth/forgot-password`,
    {
      email,
    },
  );
  return response;
};

export interface ResetPayload {
  email: string;
  password: string;
  code: string;
}
const resetPassword = async (payload: ResetPayload) => {
  const { email, password, code } = payload;
  const response = await mainApiInstance.post<ApiErrorResponse>(
    `/auth/reset-password`,
    {
      email,
      password,
      code,
    },
  );
  return response;
};

const logout = async (returnUrl?: string) => {
  trackEvent(BIEvents.LOGOUT, {
    action: "click",
  });
  await signOut({ redirect: false });
  await removeCookie();
  useAuthStore.getState().removeAuth();
  window.location.assign(returnUrl || "/auth/login");
};

const signup = async (payload: ISignupPayload) => {
  const response = await mainApiInstance.post<
    ISignupPayload | ApiErrorResponse
  >(`/auth/sign-up`, payload);
  return response;
};

const verifyOTP = async (email: string, otp: string) => {
  const response = await mainApiInstance.post<ApiErrorResponse>(
    `/auth/verify`,
    {
      email,
      code: otp,
    },
  );
  return response;
};

const resendOTP = async (email: string) => {
  const response = await mainApiInstance.post<ApiErrorResponse>(
    `/auth/resend-code?email=${email}`,
  );
  return response;
};

interface ISearchHistory {
  query: string;
  count: number;
  updatedAt: Date;
}

export interface IUser {
  userId: string;
  roleId?: string;
  address: string | null;
  avatarThumbnailUrl?: string;
  company: null;
  country: string | null;
  dob: string | null;
  facebookUrl: string | null;
  introduction: string | null;
  isVerified: boolean;
  linkedinUrl: string | null;
  resumes: Array<{
    id: string;
    title: string;
    description: string;
    bookmarked?: boolean;
  }>;
  role: string;
  status: boolean;
  userInfo: {
    email: string;
    firstName: string;
    lastName: string;
    phone: string;
  };
  points: number;
  distributionPoints: number;
  commissionPoints: number;
  searchHistory: ISearchHistory[];
  membership: {
    type: "STARTER" | "PRO" | "ULTIMATE";
    aiQuotas: number;
    quotaConversionRate: number;
    membershipPoints: number;
  };
  provider: "LINKEDIN" | "GOOGLE" | "FACEBOOK" | "EMAIL";
  paymentCustomerId: string | null;
}

const getCurrentUser = async () => {
  const auth = useAuthStore.getState().getAuth();
  if (!auth?.userId) {
    return null;
  }

  const response = await mainApiInstance.get<IUser | ApiErrorResponse>(
    `/users/${auth.userId}`,
  );
  return response.data;
};

export const authService = {
  login,
  logout,
  signup,
  verifyOTP,
  resendOTP,
  forgotPassword,
  resetPassword,
  getCurrentUser,
};
